import { useRef, useCallback } from 'react'

/**
 * Position interface
 */
interface Position {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Animation direction
 */
type Direction = 'horizontal' | 'vertical' | 'diagonal'

/**
 * Spring easing function with damping
 * Simulates spring physics for smooth, bouncy motion
 */
const createSpringEasing = (damping = 0.7, stiffness = 0.3) => {
  return `cubic-bezier(0.34, ${1 + stiffness}, ${damping}, 1)`
}

/**
 * Calculate direction and distance between two positions
 */
const calculateMovement = (from: Position, to: Position) => {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const distance = Math.sqrt(dx * dx + dy * dy)

  // Determine primary direction
  let direction: Direction = 'horizontal'
  if (Math.abs(dy) > Math.abs(dx) * 1.5) {
    direction = 'vertical'
  } else if (Math.abs(dx) > Math.abs(dy) * 1.5) {
    direction = 'horizontal'
  } else {
    direction = 'diagonal'
  }

  // Calculate angle for skew effect
  const angle = Math.atan2(dy, dx) * (180 / Math.PI)

  return { dx, dy, distance, direction, angle }
}

/**
 * Generate morphing keyframes based on direction
 */
const generateMorphingKeyframes = (
  from: Position,
  to: Position,
  direction: Direction,
  angle: number
) => {
  // Base transforms
  const fromTransform = `translate(${from.x}px, ${from.y}px) scale(1) skew(0deg)`
  const toTransform = `translate(${to.x}px, ${to.y}px) scale(1) skew(0deg)`

  // Morphing parameters based on direction
  let stretchScale = '1.15, 0.95'  // Default: stretch X, squash Y
  let overshootScale = '1.05, 1.03' // Slight overshoot
  let skewValue = '5deg'

  if (direction === 'vertical') {
    stretchScale = '0.95, 1.15'  // Stretch Y, squash X
    skewValue = '-5deg'
  } else if (direction === 'diagonal') {
    stretchScale = '1.1, 1.1'    // Equal stretch
    skewValue = `${angle * 0.1}deg` // Skew based on angle
  }

  // Keyframes with physics stages
  return [
    // Start: Idle state
    {
      transform: fromTransform,
      opacity: '0.95',
      offset: 0
    },
    // Anticipate: Slight squash before moving (15%)
    {
      transform: `translate(${from.x}px, ${from.y}px) scale(0.95) skew(0deg)`,
      opacity: '0.98',
      offset: 0.15
    },
    // Stretch: Mid-movement elongation (45%)
    {
      transform: `translate(${(from.x + to.x) / 2}px, ${(from.y + to.y) / 2}px) scale(${stretchScale}) skew(${skewValue})`,
      opacity: '1',
      offset: 0.45
    },
    // Overshoot: Go past target slightly (75%)
    {
      transform: `translate(${to.x + (to.x - from.x) * 0.1}px, ${to.y + (to.y - from.y) * 0.1}px) scale(${overshootScale}) skew(${direction === 'horizontal' ? '-2deg' : '2deg'})`,
      opacity: '1',
      offset: 0.75
    },
    // Settle: Return to normal (100%)
    {
      transform: toTransform,
      opacity: '0.95',
      offset: 1
    },
  ]
}

/**
 * Extract current position from element's computed transform
 */
const getCurrentPosition = (element: HTMLElement): { x: number; y: number } => {
  const transform = getComputedStyle(element).transform

  if (transform === 'none') {
    return { x: 0, y: 0 }
  }

  const matrix = new DOMMatrix(transform)
  return {
    x: matrix.m41,
    y: matrix.m42,
  }
}

/**
 * Generate fast mode keyframes (simple, direct movement)
 */
const generateFastKeyframes = (from: Position, to: Position) => {
  return [
    {
      transform: `translate(${from.x}px, ${from.y}px)`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      offset: 0,
    },
    {
      transform: `translate(${to.x}px, ${to.y}px)`,
      width: `${to.width}px`,
      height: `${to.height}px`,
      offset: 1,
    },
  ]
}

/**
 * Custom hook for fluid animation with spring physics
 */
export function useFluidAnimation() {
  const previousPositionRef = useRef<Position | null>(null)
  const animationRef = useRef<Animation | null>(null)
  const lastChangeTimestampRef = useRef<number>(0)

  /**
   * Animate element to new position with fluid physics
   */
  const animate = useCallback((element: HTMLElement, targetPosition: Position) => {
    // Detect fast mode (rapid changes < 200ms)
    const now = Date.now()
    const timeSinceLastChange = now - lastChangeTimestampRef.current
    const isFastMode = timeSinceLastChange < 200 && lastChangeTimestampRef.current > 0
    lastChangeTimestampRef.current = now

    // Capture current visual position before canceling
    let from: Position
    if (animationRef.current) {
      // Get actual current position from computed transform
      const currentPos = getCurrentPosition(element)
      from = {
        x: currentPos.x,
        y: currentPos.y,
        width: previousPositionRef.current?.width || targetPosition.width,
        height: previousPositionRef.current?.height || targetPosition.height,
      }

      // Cancel previous animation
      animationRef.current.cancel()
    } else {
      from = previousPositionRef.current || targetPosition
    }

    const to = targetPosition

    // Calculate movement parameters
    const { direction, angle, distance } = calculateMovement(from, to)

    // Choose keyframes and duration based on mode
    let keyframes: Keyframe[]
    let duration: number
    let easing: string

    if (isFastMode) {
      // Fast mode: Simple, direct movement
      keyframes = generateFastKeyframes(from, to)
      duration = Math.min(150, Math.max(100, distance * 0.5))
      easing = 'cubic-bezier(0.25, 0.1, 0.25, 1)' // ease-out
    } else {
      // Slow mode: Full morphing with spring physics
      keyframes = generateMorphingKeyframes(from, to, direction, angle)
      duration = Math.min(450, Math.max(350, distance * 2))
      easing = createSpringEasing(0.65, 0.4)
    }

    // Create animation
    const animation = element.animate(keyframes, {
      duration,
      easing,
      fill: 'forwards',
    })

    // Store animation reference
    animationRef.current = animation

    // Update position after animation
    animation.onfinish = () => {
      previousPositionRef.current = to
    }

    return animation
  }, [])

  /**
   * Reset animation state (for initial appearance)
   */
  const reset = useCallback(() => {
    if (animationRef.current) {
      animationRef.current.cancel()
      animationRef.current = null
    }
    previousPositionRef.current = null
    lastChangeTimestampRef.current = 0
  }, [])

  return {
    animate,
    reset,
  }
}
